import React from 'react';

import { Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import BdsCardContainer from '../../../../shared/components/v3/Cards/BdsCardContainer/BdsCardContainer';
import ErrorCard from '../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import { EMAIL_ICON } from '../../../../shared/constants/images';
import { ICollaboratorContactEmail } from '../../../../shared/interfaces/collaborators';
import { TRANSLATION_COLLABORATORS } from '../../../../shared/translations/collaborators';
import { TRANSLATIONS_GLOBAL } from '../../../../shared/translations/commons';

import './ContactData.scss';

interface IContactData {
  emails: ICollaboratorContactEmail[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

const ContactData = ({ emails, isLoading, isError, onRetry }: IContactData) => {
  const { t } = useTranslation();

  if (isError) {
    return (
      <div className="flex flex-col justify-center items-center w-full">
        <ErrorCard
          primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
          secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
          center={true}
          action={onRetry}
          className="flex flex-col justify-center items-center"
          buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
        />
      </div>
    );
  }

  return (
    <BdsCardContainer
      className="collaborator-contact-data-section flex flex-col w-full"
      paddingSize="padding-l"
      type="solid"
      borderRadiusSize="radius-l"
    >
      <div className="title">{t(TRANSLATION_COLLABORATORS.detail.contactData.title)}</div>
      <div className="emails flex flex-col w-full">
        <span className="label">{t(TRANSLATION_COLLABORATORS.detail.contactData.emailsLabel)}</span>
        {isLoading && <Skeleton animation={false} className="skeleton" variant="rectangular" />}
        {!isLoading && emails.length === 0 && (
          <span className="empty">{t(TRANSLATION_COLLABORATORS.detail.contactData.emailsEmpty)}</span>
        )}
        {!isLoading && emails.length > 0 && (
          <ul className="email-list flex flex-col w-full" data-testid="contact-email-list">
            {emails.map((item) => (
              <li key={item.email} className="email-row flex flex-row items-center">
                <EMAIL_ICON.component className="email-icon" />
                <span className="email-value" title={item.email}>
                  {item.email}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </BdsCardContainer>
  );
};

export default ContactData;
